/* ==========================================================================
   Sistema visual — hierro y tiza
   ==========================================================================

   Dirección: el peso en este dominio está CUANTIZADO. Una máquina que sube de 5 en 5 no
   admite 2,5 lb; las sesiones de un plan son enteras; los días de colchón son enteros. Por
   eso acá no hay ni una barra de progreso continua: toda proporción se dibuja como placas
   discretas apiladas (`.pila`). Además de ser fiel al objeto, evita insinuar una precisión
   que la bioimpedancia no tiene (±3–5 puntos frente a un DEXA).

   Tipografía como señal, y esta es la regla que no hay que romper:
     .lectura  → monoespaciada. TODO lo que el usuario leyó de una máquina o de una balanza.
     el resto  → tipografía de UI. TODO lo que dice la app.
   La tipografía distingue una medición de una interpretación, que es exactamente la
   distinción que §5.3 exige que la app nunca borre.

   Sin fuentes web: la app tiene que abrir sin señal, y una fuente que no carga es una app
   que no se lee. El carácter sale del tratamiento, no del archivo.
   ========================================================================== */

:root {
  /* Hierro fundido: oscuro pero con temperatura, no negro puro. */
  --hierro: #16171b;
  --hierro-alto: #1e2027;
  --hierro-borde: #2b2d36;
  --hierro-hundido: #101116;

  /* Tiza de gimnasio: blanco cálido, no #fff. */
  --tiza: #eceae4;
  --tiza-media: #a9a7a0;
  --tiza-baja: #74736e;

  /* Ámbar de placa pintada y de display de siete segmentos. Es el único acento. */
  --ambar: #d9822b;
  --ambar-tenue: #d9822b26;

  /* Agua: los estados buenos. Grasa perdida, progreso real. */
  --agua: #4a9d8f;
  --agua-tenue: #4a9d8f26;

  /* Ladrillo: alertas. Apagado a propósito — §10 prohíbe castigar. */
  --ladrillo: #c2563f;
  --ladrillo-tenue: #c2563f26;

  --f-ui: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  --f-lectura: ui-monospace, 'SF Mono', SFMono-Regular, Menlo, Consolas, monospace;

  --r-chico: 3px;
  --r: 6px;
  --r-grande: 10px;

  /* Objetivo táctil mínimo. §9: uso con una mano, brazos cansados. */
  --toque: 56px;

  --e1: 4px;
  --e2: 8px;
  --e3: 12px;
  --e4: 16px;
  --e5: 24px;
  --e6: 32px;
  --e7: 48px;

  --sombra: 0 1px 0 #ffffff08 inset, 0 2px 12px #00000040;
}

/* Tema claro, para el PC con luz. Se activa desde Ajustes; el default es oscuro porque el
   caso de uso primario es un gimnasio con poca luz. */
:root[data-tema='claro'] {
  --hierro: #f2f0ea;
  --hierro-alto: #ffffff;
  --hierro-borde: #d8d5cc;
  --hierro-hundido: #e6e3db;
  --tiza: #1a1a1c;
  --tiza-media: #55544f;
  --tiza-baja: #7d7b74;
  --ambar: #a35a12;
  --ambar-tenue: #a35a1218;
  --agua: #2f7568;
  --agua-tenue: #2f756818;
  --ladrillo: #a33723;
  --ladrillo-tenue: #a3372318;
  --sombra: 0 1px 2px #0000000f, 0 2px 8px #0000000a;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html,
body,
#raiz {
  height: 100%;
}

body {
  margin: 0;
  background: var(--hierro);
  color: var(--tiza);
  font-family: var(--f-ui);
  font-size: 16px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  /* Sin rebote elástico: en medio de una serie, un scroll accidental es un toque perdido. */
  overscroll-behavior-y: none;
}

h1,
h2,
h3,
h4,
p,
figure {
  margin: 0;
}

button,
input,
select,
textarea {
  font: inherit;
  color: inherit;
}

button {
  cursor: pointer;
}

a {
  color: var(--ambar);
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

:focus-visible {
  outline: 2px solid var(--ambar);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}

/* --------------------------------------------------------------------------
   Tipografía
   -------------------------------------------------------------------------- */

/* La regla del proyecto: valores medidos en monoespaciada, con cifras de ancho fijo para
   que una columna de pesos se lea como una columna y no como un texto. */
.lectura {
  font-family: var(--f-lectura);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  letter-spacing: -0.02em;
}

/* display:block a propósito: como span inline, la cifra que viene después se le pega en la
   misma línea y la tarjeta se lee "MASA MAGRA 66,9" en vez de título arriba y número abajo. */
.eyebrow {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--tiza-baja);
  line-height: 1.4;
}

.titulo {
  font-size: 20px;
  font-weight: 650;
  letter-spacing: -0.02em;
}

.nota {
  font-size: 13px;
  color: var(--tiza-media);
  line-height: 1.45;
}

.nota--fina {
  font-size: 12px;
  color: var(--tiza-baja);
}

/* El número héroe. Grande, apretado, con cifras tabulares. */
.cifra-heroe {
  font-family: var(--f-lectura);
  font-variant-numeric: tabular-nums;
  font-size: clamp(56px, 18vw, 96px);
  font-weight: 700;
  line-height: 0.9;
  letter-spacing: -0.05em;
}

.cifra-grande {
  font-family: var(--f-lectura);
  font-variant-numeric: tabular-nums;
  font-size: 28px;
  font-weight: 650;
  line-height: 1.1;
  letter-spacing: -0.03em;
}

.unidad {
  font-family: var(--f-ui);
  font-size: 0.42em;
  font-weight: 500;
  letter-spacing: 0;
  color: var(--tiza-media);
}

/* --------------------------------------------------------------------------
   La firma: la pila de placas
   --------------------------------------------------------------------------
   Una proporción dibujada como unidades discretas. Se usa para el p-ratio, para las series
   semanales contra el objetivo, y para las sesiones del plan. Es lo único llamativo de la
   app; todo lo demás se mantiene callado. */

/* align-items: stretch y no flex-end: las placas no tienen altura propia, así que con flex-end
   colapsan a 0 px y la pila entera queda invisible. El alto lo pone el contenedor. */
.pila {
  display: flex;
  gap: 3px;
  align-items: stretch;
}

.pila--vertical {
  flex-direction: column-reverse;
  gap: 2px;
}

.placa {
  flex: 1 1 0;
  min-width: 4px;
  border-radius: 1px;
  background: var(--hierro-borde);
  transition: background-color 120ms ease-out;
}

.pila--vertical .placa {
  width: 100%;
  flex: 0 0 auto;
}

.placa--llena {
  background: var(--agua);
}

.placa--parcial {
  background: linear-gradient(to top, var(--agua) 50%, var(--hierro-borde) 50%);
}

.placa--ambar {
  background: var(--ambar);
}

.placa--alerta {
  background: var(--ladrillo);
}

.placa--hueca {
  background: transparent;
  box-shadow: inset 0 0 0 1px var(--hierro-borde);
}

/* --------------------------------------------------------------------------
   Superficies
   -------------------------------------------------------------------------- */

.tarjeta {
  background: var(--hierro-alto);
  border: 1px solid var(--hierro-borde);
  border-radius: var(--r-grande);
  padding: var(--e4);
  box-shadow: var(--sombra);
}

.tarjeta--plana {
  box-shadow: none;
  background: transparent;
}

.fila {
  display: flex;
  align-items: center;
  gap: var(--e3);
}

.fila--entre {
  justify-content: space-between;
}

.columna {
  display: flex;
  flex-direction: column;
}

.separador {
  height: 1px;
  background: var(--hierro-borde);
  border: 0;
  margin: var(--e4) 0;
}

/* --------------------------------------------------------------------------
   Controles
   -------------------------------------------------------------------------- */

.boton {
  min-height: var(--toque);
  padding: 0 var(--e5);
  border: 1px solid var(--hierro-borde);
  border-radius: var(--r);
  background: var(--hierro-alto);
  color: var(--tiza);
  font-weight: 600;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--e2);
  transition: transform 80ms ease-out, background-color 120ms;
}

.boton:active {
  transform: scale(0.985);
}

.boton:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.boton--principal {
  background: var(--ambar);
  border-color: var(--ambar);
  color: #16171b;
}

.boton--fantasma {
  background: transparent;
}

.boton--peligro {
  border-color: var(--ladrillo);
  color: var(--ladrillo);
  background: transparent;
}

.boton--chico {
  min-height: 38px;
  padding: 0 var(--e3);
  font-size: 14px;
  font-weight: 550;
}

/* El botón de registrar una serie: el objetivo táctil más grande de la app, porque se toca
   de pie, con una mano y entre series. */
.boton--registrar {
  min-height: 72px;
  width: 100%;
  font-size: 18px;
  flex-direction: column;
  gap: 2px;
}

.campo {
  min-height: var(--toque);
  width: 100%;
  padding: 0 var(--e3);
  background: var(--hierro-hundido);
  border: 1px solid var(--hierro-borde);
  border-radius: var(--r);
  color: var(--tiza);
  font-family: var(--f-lectura);
  font-variant-numeric: tabular-nums;
  font-size: 17px;
}

.campo--texto {
  font-family: var(--f-ui);
  font-variant-numeric: normal;
}

.campo:focus {
  border-color: var(--ambar);
  outline: none;
}

.campo--revisar {
  border-color: var(--ambar);
  background: var(--ambar-tenue);
}

.etiqueta {
  display: block;
  margin-bottom: var(--e1);
}

/* Stepper de a incrementos reales de placa. */
.stepper {
  display: grid;
  grid-template-columns: var(--toque) 1fr var(--toque);
  gap: var(--e2);
  align-items: stretch;
}

.stepper .boton {
  padding: 0;
  font-size: 22px;
  font-family: var(--f-lectura);
}

/* --------------------------------------------------------------------------
   Avisos y estados
   -------------------------------------------------------------------------- */

.aviso {
  border-left: 3px solid var(--tiza-baja);
  padding: var(--e3) var(--e4);
  border-radius: 0 var(--r) var(--r) 0;
  background: var(--hierro-alto);
  font-size: 14px;
  line-height: 1.45;
}

.aviso--importante {
  border-left-color: var(--ladrillo);
  background: var(--ladrillo-tenue);
}

.aviso--atencion {
  border-left-color: var(--ambar);
  background: var(--ambar-tenue);
}

.aviso--info {
  border-left-color: var(--agua);
  background: var(--agua-tenue);
}

.insignia {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 7px;
  border-radius: var(--r-chico);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: var(--hierro-borde);
  color: var(--tiza-media);
}

.insignia--ambar {
  background: var(--ambar-tenue);
  color: var(--ambar);
}

.insignia--agua {
  background: var(--agua-tenue);
  color: var(--agua);
}

.insignia--alerta {
  background: var(--ladrillo-tenue);
  color: var(--ladrillo);
}

/* La franja de acción de Inicio: qué toca hoy y el botón para arrancarlo, arriba de todo.
   A propósito NO lleva cifra grande. §5.3 exige que el número grande de Inicio sea la calidad de
   la pérdida y no el peso; esto tiene que leerse como una puerta, no como un KPI que compite.

   La superficie es la de una tarjeta normal y no ámbar: el énfasis lo lleva el botón, que ya es
   ámbar y mide 72 px. Con la franja también en ámbar chocaba con la tarjeta de primeros pasos,
   que es ámbar por lo mismo, y dos urgencias juntas no son ninguna. */
.banda-hoy {
  display: flex;
  flex-direction: column;
  gap: var(--e3);
  padding: var(--e4);
  border: 1px solid var(--hierro-borde);
  background: var(--hierro-alto);
  border-radius: var(--r-grande);
  box-shadow: var(--sombra);
}

.banda-hoy--ancha {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: var(--e5);
}

.banda-hoy--ancha .boton {
  flex: none;
  min-width: 260px;
}

.vacio {
  padding: var(--e6) var(--e4);
  text-align: center;
  color: var(--tiza-media);
  font-size: 14px;
  line-height: 1.5;
}

/* El indicador de sincronización de la cabecera, en las dos variantes. `position: relative` para
   colgarle el detalle del fallo justo abajo: es el único lugar donde el usuario ya está mirando.
   `flex: none` porque en una cabecera angosta el que tiene que ceder es el título, no el estado. */
.sync {
  position: relative;
  display: flex;
  flex: none;
  white-space: nowrap;
}

/* Un sync que falló no puede verse igual que uno que anduvo. Va en ladrillo, que es el único color
   que la app usa para "esto no salió"; la etiqueta necesita la regla propia porque `.nota--fina`
   fija su color y le ganaría a la herencia. */
.sync--mal,
.sync--mal .nota--fina {
  color: var(--ladrillo);
}

.sync-panel {
  position: absolute;
  top: calc(100% + var(--e2));
  right: 0;
  z-index: 30;
  width: 320px;
  /* Fondo opaco propio: los avisos se tiñen con un color translúcido, y sin una superficie abajo
     el texto del panel se leería contra el contenido de la pantalla. */
  background: var(--hierro-alto);
  border: 1px solid var(--hierro-borde);
  border-radius: var(--r);
  box-shadow: var(--sombra);
  overflow: hidden;
}

/* --------------------------------------------------------------------------
   Tablas (sobre todo desktop)
   -------------------------------------------------------------------------- */

.tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.tabla th {
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--tiza-baja);
  padding: var(--e2) var(--e3);
  border-bottom: 1px solid var(--hierro-borde);
  white-space: nowrap;
}

.tabla td {
  padding: var(--e2) var(--e3);
  border-bottom: 1px solid var(--hierro-borde);
  vertical-align: middle;
}

.tabla tbody tr:hover {
  background: var(--hierro-hundido);
}

.tabla .num {
  font-family: var(--f-lectura);
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.desborda-x {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* ==========================================================================
   SHELL MOBILE — gimnasio: una mano, poca luz, brazos cansados
   ========================================================================== */

.m-app {
  display: grid;
  grid-template-rows: auto 1fr auto;
  height: 100%;
  max-width: 560px;
  margin: 0 auto;
}

.m-cabecera {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--e3);
  padding: max(var(--e3), env(safe-area-inset-top)) var(--e4) var(--e3);
  border-bottom: 1px solid var(--hierro-borde);
  background: var(--hierro);
  position: sticky;
  top: 0;
  z-index: 10;
}

/* El título es lo único que cede en la cabecera: si en un teléfono angosto no entra todo, se corta
   con puntos suspensivos en vez de partir la cabecera en dos líneas. */
.m-cabecera .titulo {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* En el celular el panel de sync no puede colgar del botón: el botón está a 100 px del borde
   derecho y un panel de 320 px se saldría de la pantalla por la izquierda. Se cuelga de la
   cabecera entera, a lo ancho, que además es donde mejor se lee un aviso. */
.m-cabecera .sync {
  position: static;
}

.m-cabecera .sync-panel {
  left: var(--e4);
  right: var(--e4);
  width: auto;
}

.m-cuerpo {
  overflow-y: auto;
  padding: var(--e4);
  padding-bottom: var(--e6);
  display: flex;
  flex-direction: column;
  gap: var(--e4);
}

/* Nav inferior: al alcance del pulgar. Nunca arriba. */
.m-tabs {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  border-top: 1px solid var(--hierro-borde);
  background: var(--hierro);
  padding-bottom: env(safe-area-inset-bottom);
}

.m-tab {
  min-height: var(--toque);
  border: 0;
  background: transparent;
  color: var(--tiza-baja);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: var(--e2) 0;
}

.m-tab[aria-current='page'] {
  color: var(--ambar);
}

.m-tab svg {
  width: 22px;
  height: 22px;
}

/* Hoja inferior: donde se registra la serie. Sube desde abajo, al pulgar. */
.m-hoja-fondo {
  position: fixed;
  inset: 0;
  background: #000000a8;
  z-index: 40;
  animation: aparecer 140ms ease-out;
}

.m-hoja {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 41;
  background: var(--hierro-alto);
  border-top: 1px solid var(--hierro-borde);
  border-radius: var(--r-grande) var(--r-grande) 0 0;
  padding: var(--e4);
  padding-bottom: calc(var(--e4) + env(safe-area-inset-bottom));
  max-height: 88vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--e4);
  animation: subir 180ms cubic-bezier(0.16, 1, 0.3, 1);
  max-width: 560px;
  margin: 0 auto;
}

/* La misma hoja, centrada, para el PC: en un monitor de 27" un panel pegado al borde inferior
   queda lejos del ojo y del mouse. Reemplaza la animación además de la posición — `subir` anima
   un translateY que pelearía con el translate del centrado. */
.hoja-centro {
  top: 50%;
  bottom: auto;
  left: 50%;
  right: auto;
  transform: translate(-50%, -50%);
  width: min(620px, calc(100vw - var(--e6)));
  max-width: none;
  max-height: 80vh;
  border: 1px solid var(--hierro-borde);
  border-radius: var(--r-grande);
  animation: aparecer 140ms ease-out;
}

@keyframes subir {
  from {
    transform: translateY(14px);
    opacity: 0.6;
  }
}

@keyframes aparecer {
  from {
    opacity: 0;
  }
}

/* ==========================================================================
   SHELL DESKTOP — escritorio: denso, mouse y teclado, ancho aprovechado
   ========================================================================== */

.d-app {
  display: grid;
  grid-template-columns: 232px 1fr;
  height: 100%;
}

.d-lateral {
  border-right: 1px solid var(--hierro-borde);
  padding: var(--e5) var(--e3);
  display: flex;
  flex-direction: column;
  gap: var(--e2);
  background: var(--hierro-hundido);
  overflow-y: auto;
}

.d-marca {
  padding: 0 var(--e3) var(--e5);
  display: flex;
  align-items: baseline;
  gap: var(--e2);
}

.d-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Los grupos de la barra lateral (Registrar / Mirar). Nueve destinos seguidos hay que leerlos
   todos para encontrar uno; en dos grupos de cuatro y tres, se salta al que corresponde. */
.d-nav-grupo {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.d-nav-rotulo {
  padding: var(--e4) var(--e3) var(--e1);
}

.d-nav-item {
  display: flex;
  align-items: center;
  gap: var(--e3);
  min-height: 40px;
  padding: 0 var(--e3);
  border: 0;
  border-radius: var(--r);
  background: transparent;
  color: var(--tiza-media);
  font-size: 14px;
  font-weight: 550;
  text-align: left;
  width: 100%;
}

.d-nav-item:hover {
  background: var(--hierro-alto);
  color: var(--tiza);
}

.d-nav-item[aria-current='page'] {
  background: var(--ambar-tenue);
  color: var(--ambar);
}

.d-nav-item svg {
  width: 17px;
  height: 17px;
  flex: none;
}

.d-nav-tecla {
  margin-left: auto;
  font-family: var(--f-lectura);
  font-size: 11px;
  color: var(--tiza-baja);
  border: 1px solid var(--hierro-borde);
  border-radius: var(--r-chico);
  padding: 0 4px;
  line-height: 1.5;
}

.d-principal {
  overflow-y: auto;
  display: grid;
  grid-template-rows: auto 1fr;
}

.d-cabecera {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--e4);
  padding: var(--e4) var(--e6);
  border-bottom: 1px solid var(--hierro-borde);
  position: sticky;
  top: 0;
  background: var(--hierro);
  z-index: 10;
}

.d-cuerpo {
  padding: var(--e6);
  display: flex;
  flex-direction: column;
  gap: var(--e5);
  max-width: 1500px;
  width: 100%;
}

/* Rejillas de escritorio: aprovechar el ancho, no estirar la vista mobile. */
.d-rejilla {
  display: grid;
  gap: var(--e4);
}

.d-rejilla--2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.d-rejilla--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.d-rejilla--4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.d-rejilla--heroe {
  grid-template-columns: minmax(320px, 420px) 1fr;
  align-items: start;
}

@media (max-width: 1180px) {
  .d-rejilla--4 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .d-rejilla--3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .d-rejilla--heroe {
    grid-template-columns: 1fr;
  }
}

/* --------------------------------------------------------------------------
   Gráficos
   --------------------------------------------------------------------------
   Sin relleno de área y sin degradados: la media móvil es una línea, y los puntos crudos van
   detrás, huecos y apagados. §5.3 pide exactamente eso — nunca mostrar una lectura suelta
   como si fuera un hecho, pero tampoco esconderla. */

.grafico {
  display: block;
  width: 100%;
  overflow: visible;
}

.g-eje {
  stroke: var(--hierro-borde);
  stroke-width: 1;
}

.g-rejilla {
  stroke: var(--hierro-borde);
  stroke-width: 1;
  stroke-dasharray: 2 4;
}

.g-etiqueta {
  fill: var(--tiza-baja);
  font-size: 10px;
  font-family: var(--f-lectura);
}

.g-media {
  fill: none;
  stroke: var(--tiza);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.g-media--agua {
  stroke: var(--agua);
}

.g-media--ambar {
  stroke: var(--ambar);
}

/* Menos de tres lecturas: no hay línea, hay puntos. Van sólidos y en el tono que tendría la media,
   para que se lean como el mismo dato y no como un tercer tipo de marca. */
.g-punto {
  fill: var(--tiza);
  stroke: none;
}

.g-punto--agua {
  fill: var(--agua);
}

.g-punto--ambar {
  fill: var(--ambar);
}

.g-crudo {
  fill: none;
  stroke: var(--tiza-baja);
  stroke-width: 1;
  opacity: 0.5;
}

.g-barra {
  fill: var(--hierro-borde);
}

.g-barra--objetivo {
  fill: var(--ambar);
}

/* --------------------------------------------------------------------------
   Utilidades
   -------------------------------------------------------------------------- */

.solo-lectores {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.crece {
  flex: 1;
}

.centrado {
  display: grid;
  place-items: center;
  min-height: 100%;
  padding: var(--e5);
}

.ancho-forma {
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  gap: var(--e4);
}

.apagado {
  color: var(--tiza-baja);
}

.tachado {
  text-decoration: line-through;
  color: var(--tiza-baja);
}
